import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Heart, Globe, Leaf, Share2, RotateCcw, Home, Check } from 'lucide-react';
import { getPersonalities, sharePersonalityResult } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ResultsPage = () => {
  const { personality } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isAnimating, setIsAnimating] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [shared, setShared] = useState(false);
  const [personalityData, setPersonalityData] = useState(null);
  const [loading, setLoading] = useState(true);

  const resultId = searchParams.get('id');

  const getIcon = (type) => {
    switch (type) {
      case 'comfort': return <Heart className="w-12 h-12 text-white" />;
      case 'adventurous': return <Globe className="w-12 h-12 text-white" />;
      case 'healthy': return <Leaf className="w-12 h-12 text-white" />;
      default: return <Heart className="w-12 h-12 text-white" />;
    }
  };

  useEffect(() => {
    const fetchPersonalityData = async () => {
      try {
        const personalities = await getPersonalities();
        setPersonalityData(personalities[personality]);
      } catch (error) {
        console.error('Failed to fetch personality data:', error);
        toast({
          title: "Error",
          description: "Failed to load personality data.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalityData();

    // Animation sequence
    setTimeout(() => setIsAnimating(false), 1000);
    setTimeout(() => setShowContent(true), 1500);
  }, [personality, toast]);

  const handleShare = async () => {
    try {
      if (resultId) {
        await sharePersonalityResult(resultId);
      }

      if (navigator.share) {
        await navigator.share({
          title: 'Best Food Buddy - My Food Personality',
          text: `I'm a ${personalityData.title}! ${personalityData.description}`,
          url: window.location.href,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(
          `I'm a ${personalityData.title}! ${personalityData.description} Check out Best Food Buddy: ${window.location.origin}`
        );
      }
      
      setShared(true);
      toast({
        title: "Shared successfully!",
        description: "Your food personality has been shared.",
      });
    } catch (err) {
      console.log('Error sharing:', err);
      toast({
        title: "Share failed",
        description: "Unable to share at this time.",
        variant: "destructive",
      });
    }
  };

  const handleRetakeTest = () => {
    navigate('/test');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-lg text-gray-600 mb-4">Loading your food personality...</p>
        </Card>
      </div>
    );
  }

  if (!personalityData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-lg text-gray-600 mb-4">Personality not found</p>
          <Button onClick={handleGoHome}>Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Animated Result Header */}
        <div className="text-center mb-12">
          <div className={`transform transition-all duration-1000 ${
            isAnimating ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
          }`}>
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r ${personalityData.color} mb-6 shadow-lg`}>
              {getIcon(personality)}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              You're a {personalityData.title}!
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {personalityData.description}
            </p>
          </div>
        </div>

        {/* Content Cards */}
        <div className={`space-y-8 transform transition-all duration-1000 delay-500 ${
          showContent ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          
          {/* Personality Traits */}
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Your Food Personality Traits
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {personalityData.traits.map((trait, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${personalityData.color}`}></div>
                  <span className="text-gray-700">{trait}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Recommended Foods */}
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Perfect Foods for You
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {personalityData.foods.map((food, index) => (
                <Badge 
                  key={index} 
                  className={`p-3 text-center justify-center bg-gradient-to-r ${personalityData.color} text-white border-0 hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
                >
                  {food}
                </Badge>
              ))}
            </div>
          </Card>

          {/* Food Image Showcase */}
          <Card className="overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <div className="aspect-video relative">
              <img 
                src={personalityData.image}
                alt={personalityData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Your Food Style</h3>
                <p className="text-lg opacity-90">Foods that match your personality perfectly</p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={handleShare}
              className={`flex items-center gap-2 px-8 py-3 text-lg transition-all duration-300 ${
                shared 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
              }`}
            >
              {shared ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
              {shared ? 'Shared!' : 'Share My Results'}
            </Button>
            
            <Button 
              onClick={handleRetakeTest}
              variant="outline"
              className="flex items-center gap-2 px-8 py-3 text-lg border-2 hover:bg-gray-50"
            >
              <RotateCcw className="w-5 h-5" />
              Retake Test
            </Button>
            
            <Button 
              onClick={handleGoHome}
              variant="outline"
              className="flex items-center gap-2 px-8 py-3 text-lg border-2 hover:bg-gray-50"
            >
              <Home className="w-5 h-5" />
              Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;